import { motion } from 'framer-motion';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, Cpu, Sparkles, Shield, Cloud, Users2, UserCog, GraduationCap, Lightbulb, Crown, Info, TrendingUp, BookOpen, Brain, Rocket, Workflow, Code, Phone, Building2 } from 'lucide-react';
import PRIsMEOverviewModal from '../components/PRIsMEOverviewModal';
import LeadershipPRIsMEModal from '../components/LeadershipPRIsMEModal';
import AméliorationContinueModal from '../components/AméliorationContinueModal';
import CadreReferentielModal from '../components/CadreReferentielModal';
import PhilosophieCroissanceModal from '../components/PhilosophieCroissanceModal';
import MethodologiePRIsMEModal from '../components/MethodologiePRIsMEModal';
import QuiEstBZModal from '../components/QuiEstBZModal';
import AikupModal from '../components/AikupModal';
import ExpertAvatar from '../components/ExpertAvatar';
import type { AikupItem } from '../data/aikupData';

const Accueil = () => {
  const navigate = useNavigate();
  const [isOverviewOpen, setIsOverviewOpen] = useState(false);
  const [isLeadershipOpen, setIsLeadershipOpen] = useState(false);
  const [isAméliorationOpen, setIsAméliorationOpen] = useState(false);
  const [isCadreReferentielOpen, setIsCadreReferentielOpen] = useState(false);
  const [isPhilosophieOpen, setIsPhilosophieOpen] = useState(false);
  const [isMethodologieOpen, setIsMethodologieOpen] = useState(false);
  const [isQuiEstBZOpen, setIsQuiEstBZOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<AikupItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5 }}
        className="relative min-h-[70vh] flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-orange-50 py-20"
      >
        <div className="text-center px-6 max-w-4xl">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 1 }}
            className="flex flex-col items-center"
          >
            <img 
              src="/PrismeLogo.png" 
              alt="PRIsME Logo" 
              className="h-32 md:h-40 w-auto mb-6 drop-shadow-2xl"
            />
            <p className="text-xl md:text-2xl text-blue-700 font-semibold mb-8">
              Plateforme interne BZ Inc.
            </p>
          </motion.div>

          <motion.p
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1, duration: 1 }}
            className="font-display text-2xl md:text-3xl text-gray-700 mb-12 italic leading-relaxed"
          >
            "L'outil propriétaire de BZ Inc. pour une excellence organisationnelle à tous les niveaux"
          </motion.p>

          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.5, duration: 1 }}
            className="space-y-4"
          >
            <p className="text-lg text-gray-600 mb-2">
              PRIsME est la plateforme interne de BZ Inc. qui centralise nos méthodologies propriétaires,
              <br />
              nos standards d'excellence et notre expertise de 25 ans en transformation TI.
            </p>
          </motion.div>
        </div>

        {/* Floating icons animation */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ y: '100%', x: `${Math.random() * 100}%`, opacity: 0 }}
              animate={{
                y: '-100%',
                opacity: [0, 0.3, 0],
              }}
              transition={{
                duration: 10 + i * 2,
                repeat: Infinity,
                delay: i * 2,
              }}
              className="absolute"
            >
              <Sparkles className="text-blue-300" size={24} />
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Gouvernance */}
      <section className="pt-6 pb-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="font-display text-4xl text-center text-blue-900 mb-16"
          >
            Gouvernance BZ simple et directe
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Dany - CA/CE */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="flex flex-col items-center">
                {/* Avatar Dany */}
                <div className="relative mb-8">
                  <motion.div
                    animate={{
                      y: [0, -10, 0],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <img 
                      src="/DB.png" 
                      alt="Dany - CA/CE"
                      className="w-32 h-32 rounded-full ring-4 ring-orange-400 shadow-2xl object-cover"
                      style={{ objectPosition: 'center 60%' }}
                    />
                  </motion.div>
                </div>

                {/* Bulle de BD style - Dany */}
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  viewport={{ once: true }}
                  className="relative bg-gradient-to-br from-orange-50 to-orange-100 rounded-3xl p-6 shadow-xl border-2 border-orange-200"
                >
                  {/* Triangle de la bulle */}
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-b-[20px] border-b-orange-100"></div>
                  
                  <div className="text-center">
                    <p className="font-display text-xl text-orange-800 mb-2">
                      Dany - CA/CE 🧡
                    </p>
                    <p className="text-gray-700 leading-relaxed italic text-lg">
                      "Une ligne claire: les gestionnaires remontent directement à Dany pour les décisions transversales."
                    </p>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Gestionnaires directs */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="flex flex-col items-center">
                {/* Icône gestionnaires */}
                <div className="relative mb-8">
                  <motion.div
                    animate={{
                      y: [0, -10, 0],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 0.5
                    }}
                  >
                    <div className="w-32 h-32 rounded-full ring-4 ring-red-400 shadow-2xl bg-red-50 flex items-center justify-center">
                      <Users2 className="w-16 h-16 text-red-700" aria-label="Gestionnaires directs" />
                    </div>
                  </motion.div>
                </div>

                {/* Bulle de BD style - Gestionnaires */}
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  viewport={{ once: true }}
                  className="relative bg-gradient-to-br from-red-50 to-red-100 rounded-3xl p-6 shadow-xl border-2 border-red-200"
                >
                  {/* Triangle de la bulle */}
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-b-[20px] border-b-red-100"></div>
                  
                  <div className="text-center">
                    <p className="font-display text-xl text-red-800 mb-2">
                      Gestionnaires directs ❤️
                    </p>
                    <p className="text-gray-700 leading-relaxed italic text-lg">
                      "Ventes, marketing, opérations, finances, RH et TI: chaque responsable a une ligne directe vers Dany."
                    </p>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* François - VP Technologique */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="flex flex-col items-center">
                {/* Avatar François */}
                <div className="relative mb-8">
                  <motion.div
                    animate={{
                      y: [0, -10, 0],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 1
                    }}
                  >
                    <img 
                      src="/FP.png" 
                      alt="François - VP Technologique"
                      className="w-32 h-32 rounded-full ring-4 ring-blue-400 shadow-2xl"
                    />
                  </motion.div>
                </div>

                {/* Bulle de BD style - François */}
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                  viewport={{ once: true }}
                  className="relative bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl p-6 shadow-xl border-2 border-blue-200"
                >
                  {/* Triangle de la bulle */}
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-b-[20px] border-b-blue-100"></div>
                  
                  <div className="text-center">
                    <p className="font-display text-xl text-blue-800 mb-2">
                      François - VP Techno 💙
                    </p>
                    <p className="text-gray-700 leading-relaxed italic text-lg">
                      "Excellence technologique et innovation pour concrétiser vos ambitions numériques."
                    </p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Notre Approche Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-50 to-orange-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="font-display text-4xl text-center text-blue-900 mb-12"
          >
            Notre Approche Collaborative
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-center text-gray-600 text-lg mb-12 max-w-3xl mx-auto"
          >
            Chaque concept que nous présentons est enrichi par les perspectives complémentaires 
            de nos trois experts. Cette approche holistique garantit des solutions complètes et équilibrées.
          </motion.p>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-6 shadow-lg text-center"
            >
              <Target className="mx-auto mb-4 text-orange-500" size={48} />
              <h3 className="font-display text-xl text-orange-800 mb-3">Vision Stratégique</h3>
              <p className="text-gray-600">
                Alignement des initiatives TI avec vos objectifs d'affaires
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-6 shadow-lg text-center"
            >
              <Sparkles className="mx-auto mb-4 text-red-500" size={48} />
              <h3 className="font-display text-xl text-red-800 mb-3">Innovation Continue</h3>
              <p className="text-gray-600">
                Identification d'opportunités de croissance et de différenciation
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-6 shadow-lg text-center"
            >
              <Cpu className="mx-auto mb-4 text-blue-500" size={48} />
              <h3 className="font-display text-xl text-blue-800 mb-3">Excellence Technique</h3>
              <p className="text-gray-600">
                Solutions robustes et évolutives adaptées à vos besoins
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Départements - Quick Links */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-50 via-white to-orange-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-4xl text-center text-blue-900 mb-4">
            Nos Départements
          </h2>
          <p className="text-center text-gray-600 text-lg mb-12 max-w-3xl mx-auto">
            PRIsME est l'outil interne de BZ Inc. qui s'étend à tous les niveaux de notre organisation. 
            Explorez les 18 piliers d'excellence BZ de chaque département, basés sur nos méthodologies propriétaires et nos 25 ans d'expérience.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Service & Direction TI */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              onClick={() => navigate('/direction-ti')}
              className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2"
            >
              <div className="text-white">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                    <Target className="w-8 h-8" />
                  </div>
                </div>
                <h3 className="font-display text-2xl text-center mb-4">Service & Direction TI</h3>
                <p className="text-blue-100 text-center">
                  Les 18 piliers de l'excellence technologique et solutions d'affaires pour une transformation numérique réussie.
                </p>
              </div>
            </motion.div>

            {/* Ventes & Marketing */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              onClick={() => navigate('/directeur-compte')}
              className="bg-gradient-to-br from-red-500 to-red-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2"
            >
              <div className="text-white">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                    <Target className="w-8 h-8" />
                  </div>
                </div>
                <h3 className="font-display text-2xl text-center mb-4">Ventes & Marketing</h3>
                <p className="text-red-100 text-center">
                  Les 18 piliers du développement commercial et excellence en vente pour une croissance durable et authentique.
                </p>
              </div>
            </motion.div>

            {/* Cybersécurité */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              onClick={() => navigate('/solutions-affaires')}
              className="bg-gradient-to-br from-purple-500 to-purple-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2"
            >
              <div className="text-white">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                    <Shield className="w-8 h-8" />
                  </div>
                </div>
                <h3 className="font-display text-2xl text-center mb-4">Cybersécurité</h3>
                <p className="text-purple-100 text-center">
                  Protection et conformité selon les standards NIST, ISO 27001 et Loi 25 pour sécuriser votre organisation.
                </p>
              </div>
            </motion.div>

            {/* Gestionnaires */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              onClick={() => navigate('/gestionnaires')}
              className="bg-gradient-to-br from-green-500 to-green-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2"
            >
              <div className="text-white">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                    <Users2 className="w-8 h-8" />
                  </div>
                </div>
                <h3 className="font-display text-2xl text-center mb-4">Gestionnaires</h3>
                <p className="text-green-100 text-center">
                  Leadership et gestion d'équipe pour optimiser la performance et développer l'excellence opérationnelle.
                </p>
              </div>
            </motion.div>

            {/* Infonuagique (Cloud) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              onClick={() => navigate('/infonuagique')}
              className="bg-gradient-to-br from-cyan-500 to-cyan-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2"
            >
              <div className="text-white">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                    <Cloud className="w-8 h-8" />
                  </div>
                </div>
                <h3 className="font-display text-2xl text-center mb-4">Infonuagique (Cloud)</h3>
                <p className="text-cyan-100 text-center">
                  Migration, architecture et optimisation cloud pour moderniser votre infrastructure et réduire les coûts.
                </p>
              </div>
            </motion.div>

            {/* Programmation & IA */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.45 }}
              viewport={{ once: true }}
              onClick={() => navigate('/programmation-ia')}
              className="bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2"
            >
              <div className="text-white">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                    <Code className="w-8 h-8" />
                  </div>
                </div>
                <h3 className="font-display text-2xl text-center mb-4">Programmation & IA</h3>
                <p className="text-indigo-100 text-center">
                  Développement d'applications avec IA intégrée. Cadre PROGRAM-IA : POC 48h, Overview in-app, Chatbot standard.
                </p>
              </div>
            </motion.div>

            {/* BZ Telecom */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
              onClick={() => navigate('/bz-telecom')}
              className="bg-gradient-to-br from-orange-500 to-orange-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2"
            >
              <div className="text-white">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                    <Phone className="w-8 h-8" />
                  </div>
                </div>
                <h3 className="font-display text-2xl text-center mb-4">BZ Telecom</h3>
                <p className="text-orange-100 text-center">
                  Téléphonie Cloud Asterisk avec service humain inclus. Excellence télécom BZ Inc. - Partenariat stratégique BZ/BZ Telecom.
                </p>
              </div>
            </motion.div>

            {/* Administration & RH */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.55 }}
              viewport={{ once: true }}
              onClick={() => navigate('/admin-rh')}
              className="bg-gradient-to-br from-orange-500 to-orange-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2"
            >
              <div className="text-white">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                    <UserCog className="w-8 h-8" />
                  </div>
                </div>
                <h3 className="font-display text-2xl text-center mb-4">Administration & RH</h3>
                <p className="text-orange-100 text-center">
                  Les 18 piliers de l'excellence humaine et opérationnelle pour une organisation performante et harmonieuse.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section Top 3 & Vue d'ensemble */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-50 via-white to-orange-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-4xl text-center text-blue-900 mb-4">
            Découvrir PRIsME
          </h2>
          <p className="text-center text-gray-600 text-lg mb-12 max-w-3xl mx-auto">
            Explorez les rôles visionnaires et la vue d'ensemble complète de la plateforme PRIsME.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 mb-20">
            {/* Leadership PRIsME - Top 3 + Leaders */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02, y: -5 }}
              onClick={() => setIsLeadershipOpen(true)}
              className="relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer h-64 group"
            >
              {/* Image de fond */}
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ 
                  backgroundImage: `url(https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&auto=format&fit=crop)`,
                }}
              />
              
              {/* Overlay gradient pour lisibilité */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30 group-hover:from-purple-600/95 group-hover:via-orange-600/70 transition-all duration-300" />
              
              {/* Badge AI - Disponible dans AI Assistant */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                title="Disponible dans AI Assistant"
              >
                <Brain className="w-4 h-4 text-purple-200" />
                <span className="text-xs text-purple-100 font-semibold">AI</span>
              </motion.div>
              
              {/* Contenu */}
              <div className="relative h-full p-6 flex flex-col justify-end text-white">
                <div className="flex items-center gap-3 mb-3">
                  <Crown className="w-8 h-8 drop-shadow-lg" />
                  <h3 className="text-xl font-bold drop-shadow-lg">Leadership PRIsME</h3>
                </div>
                <p className="text-white/90 text-sm leading-relaxed drop-shadow">
                  Le Top 3 Visionnaires + 11 Leaders par département qui portent PRIsME sur le terrain. Découvrez leur vision, leur impact et leur engagement.
                </p>
              </div>
              
              {/* Effet de brillance au hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent" />
              </div>
            </motion.div>

            {/* Vue d'ensemble */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.05 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02, y: -5 }}
              onClick={() => setIsOverviewOpen(true)}
              className="relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer h-64 group"
            >
              {/* Image de fond */}
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ 
                  backgroundImage: `url(https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&auto=format&fit=crop)`,
                }}
              />
              
              {/* Overlay gradient pour lisibilité */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30 group-hover:from-blue-600/95 group-hover:via-purple-600/70 transition-all duration-300" />
              
              {/* Badge AI - Disponible dans AI Assistant */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                title="Disponible dans AI Assistant"
              >
                <Brain className="w-4 h-4 text-purple-200" />
                <span className="text-xs text-purple-100 font-semibold">AI</span>
              </motion.div>
              
              {/* Contenu */}
              <div className="relative h-full p-6 flex flex-col justify-end text-white">
                <div className="flex items-center gap-3 mb-3">
                  <Info className="w-8 h-8 drop-shadow-lg" />
                  <h3 className="text-xl font-bold drop-shadow-lg">Vue d'ensemble PRIsME</h3>
                </div>
                <p className="text-white/90 text-sm leading-relaxed drop-shadow">
                  Documentation complète de la plateforme PRIsME : méthodologie, standards et vision stratégique.
                </p>
              </div>
              
              {/* Effet de brillance au hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section Formation, Concepts */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-50 via-white to-orange-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-4xl text-center text-blue-900 mb-4">
            Ressources & Outils
          </h2>
          <p className="text-center text-gray-600 text-lg mb-12 max-w-3xl mx-auto">
            Accédez aux ressources et outils PRIsME pour développer vos compétences.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Amélioration Continue et Rigueur */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02, y: -5 }}
              onClick={() => setIsAméliorationOpen(true)}
              className="relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer h-64 group"
            >
              {/* Image de fond */}
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ 
                  backgroundImage: `url(https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&auto=format&fit=crop)`,
                }}
              />
              
              {/* Overlay gradient pour lisibilité */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30 group-hover:from-emerald-600/95 group-hover:via-teal-600/70 transition-all duration-300" />
              
              {/* Badge AI - Disponible dans AI Assistant */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                title="Disponible dans AI Assistant"
              >
                <Brain className="w-4 h-4 text-purple-200" />
                <span className="text-xs text-purple-100 font-semibold">AI</span>
              </motion.div>
              
              {/* Avatars des experts en haut à droite */}
              <div className="absolute top-2 right-2 z-10 flex gap-1.5">
                <div className="cursor-pointer hover:scale-110 transition-transform duration-200">
                  <ExpertAvatar name="Dany" score={5} type="dany" />
                </div>
                <div className="cursor-pointer hover:scale-110 transition-transform duration-200">
                  <ExpertAvatar name="Dany" score={4} type="yannick" />
                </div>
                <div className="cursor-pointer hover:scale-110 transition-transform duration-200">
                  <ExpertAvatar name="François" score={5} type="francois" />
                </div>
              </div>
              
              {/* Contenu */}
              <div className="relative h-full p-6 flex flex-col justify-end text-white">
                <div className="flex items-center gap-3 mb-3">
                  <TrendingUp className="w-8 h-8 drop-shadow-lg" />
                  <h3 className="text-xl font-bold drop-shadow-lg">Amélioration Continue</h3>
                </div>
                <p className="text-white/90 text-sm leading-relaxed drop-shadow">
                  9 Standards d'Excellence BZ pour élever l'équipe - Documentation, IA, Logs, Kaizen. Pour tous les départements.
                </p>
              </div>
              
              {/* Effet de brillance au hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent" />
              </div>
            </motion.div>

            {/* Cadre Référentiel BZ */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02, y: -5 }}
              onClick={() => setIsCadreReferentielOpen(true)}
              className="relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer h-64 group"
            >
              {/* Image de fond */}
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ 
                  backgroundImage: `url(https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop)`,
                }}
              />
              
              {/* Overlay gradient pour lisibilité */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30 group-hover:from-violet-600/95 group-hover:via-purple-600/70 transition-all duration-300" />
              
              {/* Badge AI - Disponible dans AI Assistant */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                title="Disponible dans AI Assistant"
              >
                <Brain className="w-4 h-4 text-purple-200" />
                <span className="text-xs text-purple-100 font-semibold">AI</span>
              </motion.div>
              
              {/* Avatars des experts en haut à droite */}
              <div className="absolute top-2 right-2 z-10 flex gap-1.5">
                <div className="cursor-pointer hover:scale-110 transition-transform duration-200">
                  <ExpertAvatar name="Dany" score={5} type="dany" />
                </div>
                <div className="cursor-pointer hover:scale-110 transition-transform duration-200">
                  <ExpertAvatar name="Dany" score={5} type="yannick" />
                </div>
                <div className="cursor-pointer hover:scale-110 transition-transform duration-200">
                  <ExpertAvatar name="François" score={5} type="francois" />
                </div>
              </div>
              
              {/* Contenu */}
              <div className="relative h-full p-6 flex flex-col justify-end text-white">
                <div className="flex items-center gap-3 mb-3">
                  <BookOpen className="w-8 h-8 drop-shadow-lg" />
                  <h3 className="text-xl font-bold drop-shadow-lg">Cadre Référentiel BZ</h3>
                </div>
                <p className="text-white/90 text-sm leading-relaxed drop-shadow">
                  Évaluez et améliorez la maturité TI avec notre cadre holistique - Spider charts, roadmap structuré, standards ITIL & NIST 2.0.
                </p>
              </div>
              
              {/* Effet de brillance au hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent" />
              </div>
            </motion.div>

            {/* Formation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02, y: -5 }}
              onClick={() => navigate('/formation')}
              className="relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer h-64 group"
            >
              {/* Image de fond */}
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ 
                  backgroundImage: `url(https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&auto=format&fit=crop)`,
                }}
              />
              
              {/* Overlay gradient pour lisibilité */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30 group-hover:from-indigo-600/95 group-hover:via-indigo-600/70 transition-all duration-300" />
              
              {/* Contenu */}
              <div className="relative h-full p-6 flex flex-col justify-end text-white">
                <div className="flex items-center gap-3 mb-3">
                  <GraduationCap className="w-8 h-8 drop-shadow-lg" />
                  <h3 className="text-xl font-bold drop-shadow-lg">Formation</h3>
                </div>
                <p className="text-white/90 text-sm leading-relaxed drop-shadow">
                  Programmes de développement professionnel et parcours d'apprentissage pour maîtriser les compétences TI.
                </p>
              </div>
              
              {/* Effet de brillance au hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent" />
              </div>
            </motion.div>

            {/* Concepts */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.05 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02, y: -5 }}
              onClick={() => navigate('/concepts')}
              className="relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer h-64 group"
            >
              {/* Image de fond */}
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ 
                  backgroundImage: `url(https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&auto=format&fit=crop)`,
                }}
              />
              
              {/* Overlay gradient pour lisibilité */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30 group-hover:from-amber-600/95 group-hover:via-amber-600/70 transition-all duration-300" />
              
              {/* Badge AI - Disponible dans AI Assistant */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                title="Disponible dans AI Assistant"
              >
                <Brain className="w-4 h-4 text-purple-200" />
                <span className="text-xs text-purple-100 font-semibold">AI</span>
              </motion.div>
              
              {/* Contenu */}
              <div className="relative h-full p-6 flex flex-col justify-end text-white">
                <div className="flex items-center gap-3 mb-3">
                  <Lightbulb className="w-8 h-8 drop-shadow-lg" />
                  <h3 className="text-xl font-bold drop-shadow-lg">Concepts</h3>
                </div>
                <p className="text-white/90 text-sm leading-relaxed drop-shadow">
                  Principes fondamentaux et standards BZ Inc. pour l'excellence TI et l'excellence organisationnelle.
                </p>
              </div>
              
              {/* Effet de brillance au hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent" />
              </div>
            </motion.div>

            {/* Philosophie de Croissance BZ */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02, y: -5 }}
              onClick={() => setIsPhilosophieOpen(true)}
              className="relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer h-64 group"
            >
              {/* Image de fond */}
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ 
                  backgroundImage: `url(https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800&auto=format&fit=crop)`,
                }}
              />
              
              {/* Overlay gradient pour lisibilité */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30 group-hover:from-emerald-600/95 group-hover:via-teal-600/70 transition-all duration-300" />
              
              {/* Badge AI - Disponible dans AI Assistant */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                title="Disponible dans AI Assistant"
              >
                <Brain className="w-4 h-4 text-purple-200" />
                <span className="text-xs text-purple-100 font-semibold">AI</span>
              </motion.div>
              
              {/* Avatars des experts en haut à droite */}
              <div className="absolute top-2 right-2 z-10 flex gap-1.5">
                <div className="cursor-pointer hover:scale-110 transition-transform duration-200">
                  <ExpertAvatar name="Dany" score={5} type="dany" />
                </div>
                <div className="cursor-pointer hover:scale-110 transition-transform duration-200">
                  <ExpertAvatar name="Dany" score={4} type="yannick" />
                </div>
                <div className="cursor-pointer hover:scale-110 transition-transform duration-200">
                  <ExpertAvatar name="François" score={3} type="francois" />
                </div>
              </div>
              
              {/* Contenu */}
              <div className="relative h-full p-6 flex flex-col justify-end text-white">
                <div className="flex items-center gap-3 mb-3">
                  <Rocket className="w-8 h-8 drop-shadow-lg" />
                  <h3 className="text-xl font-bold drop-shadow-lg">Philosophie de Croissance</h3>
                </div>
                <p className="text-white/90 text-sm leading-relaxed drop-shadow">
                  22 concepts philosophiques pour devenir la meilleure version de soi-même. Du mindset à l'action, de l'égo à l'humilité.
                </p>
              </div>
              
              {/* Effet de brillance au hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent" />
              </div>
            </motion.div>

            {/* Qui est BZ */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02, y: -5 }}
              onClick={() => setIsQuiEstBZOpen(true)}
              className="relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer h-64 group"
            >
              {/* Image de fond */}
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ 
                  backgroundImage: `url(https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&auto=format&fit=crop)`,
                }}
              />
              
              {/* Overlay gradient pour lisibilité */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30 group-hover:from-blue-600/95 group-hover:via-purple-600/70 transition-all duration-300" />
              
              {/* Badge AI - Disponible dans AI Assistant */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                title="Disponible dans AI Assistant"
              >
                <Brain className="w-4 h-4 text-purple-200" />
                <span className="text-xs text-purple-100 font-semibold">AI</span>
              </motion.div>
              
              {/* Avatars des experts en haut à droite */}
              <div className="absolute top-2 right-2 z-10 flex gap-1.5">
                <div className="cursor-pointer hover:scale-110 transition-transform duration-200">
                  <ExpertAvatar name="Dany" score={5} type="dany" />
                </div>
                <div className="cursor-pointer hover:scale-110 transition-transform duration-200">
                  <ExpertAvatar name="Dany" score={5} type="yannick" />
                </div>
                <div className="cursor-pointer hover:scale-110 transition-transform duration-200">
                  <ExpertAvatar name="François" score={5} type="francois" />
                </div>
              </div>
              
              {/* Contenu */}
              <div className="relative h-full p-6 flex flex-col justify-end text-white">
                <div className="flex items-center gap-3 mb-3">
                  <Building2 className="w-8 h-8 drop-shadow-lg" />
                  <h3 className="text-xl font-bold drop-shadow-lg">Qui est BZ</h3>
                </div>
                <p className="text-white/90 text-sm leading-relaxed drop-shadow">
                  Les 4 valeurs fondamentales, la mission, la vision et l'ADN de BZ Inc. Découvrez qui nous sommes et ce qui nous guide.
                </p>
              </div>
              
              {/* Effet de brillance au hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent" />
              </div>
            </motion.div>

            {/* Méthodologie PRIsME */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.35 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02, y: -5 }}
              onClick={() => setIsMethodologieOpen(true)}
              className="relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer h-64 group"
            >
              {/* Image de fond */}
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ 
                  backgroundImage: `url(https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&auto=format&fit=crop)`,
                }}
              />
              
              {/* Overlay gradient pour lisibilité */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30 group-hover:from-purple-600/95 group-hover:via-indigo-600/70 transition-all duration-300" />
              
              {/* Badge AI - Disponible dans AI Assistant */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                title="Disponible dans AI Assistant"
              >
                <Brain className="w-4 h-4 text-purple-200" />
                <span className="text-xs text-purple-100 font-semibold">AI</span>
              </motion.div>
              
              {/* Avatars des experts en haut à droite */}
              <div className="absolute top-2 right-2 z-10 flex gap-1.5">
                <div className="cursor-pointer hover:scale-110 transition-transform duration-200">
                  <ExpertAvatar name="Dany" score={5} type="dany" />
                </div>
                <div className="cursor-pointer hover:scale-110 transition-transform duration-200">
                  <ExpertAvatar name="Dany" score={5} type="yannick" />
                </div>
                <div className="cursor-pointer hover:scale-110 transition-transform duration-200">
                  <ExpertAvatar name="François" score={5} type="francois" />
                </div>
              </div>
              
              {/* Contenu */}
              <div className="relative h-full p-6 flex flex-col justify-end text-white">
                <div className="flex items-center gap-3 mb-3">
                  <Workflow className="w-8 h-8 drop-shadow-lg" />
                  <h3 className="text-xl font-bold drop-shadow-lg">Méthodologie PRIsME</h3>
                </div>
                <p className="text-white/90 text-sm leading-relaxed drop-shadow">
                  Le workflow universel: Prise de besoin → IA Analyse → Expert Approuve → Spécifications d'exigences d'affaires et TI → Livrable tangible.
                </p>
              </div>
              
              {/* Effet de brillance au hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Modals */}
      <PRIsMEOverviewModal 
        isOpen={isOverviewOpen} 
        onClose={() => setIsOverviewOpen(false)} 
      />

      <LeadershipPRIsMEModal 
        isOpen={isLeadershipOpen} 
        onClose={() => setIsLeadershipOpen(false)} 
      />

      <AméliorationContinueModal 
        isOpen={isAméliorationOpen} 
        onClose={() => setIsAméliorationOpen(false)} 
      />

      <CadreReferentielModal 
        isOpen={isCadreReferentielOpen} 
        onClose={() => setIsCadreReferentielOpen(false)} 
      />

      <PhilosophieCroissanceModal 
        isOpen={isPhilosophieOpen} 
        onClose={() => setIsPhilosophieOpen(false)} 
      />

      <MethodologiePRIsMEModal 
        isOpen={isMethodologieOpen} 
        onClose={() => setIsMethodologieOpen(false)} 
      />

      <QuiEstBZModal 
        isOpen={isQuiEstBZOpen} 
        onClose={() => setIsQuiEstBZOpen(false)} 
      />

      <AikupModal
        item={selectedItem}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setTimeout(() => setSelectedItem(null), 300);
        }}
      />
    </div>
  );
};

export default Accueil;
